// src/pages/Sensor/Calculon.tsx

import { MoreVertTwoTone, PollTwoTone } from '@mui/icons-material'
import {
  Avatar,
  Button,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { useFilters, useSortBy, useTable } from 'react-table'

import { AM, HStack, KPage, KTextField, Preloader } from '../../components'
import { useSensorCalculations } from '../../hooks'

const columns = [
  {
    accessor: 'sensor_id',
    Header: 'Sensor ID',
    sortMethod: (a, b) => Number(a) - Number(b),
    width: 100,
  },
  {
    accessor: 'summe_monat',
    Header: 'Summe Monat',
  },
  {
    accessor: 'summe_tag',
    Header: 'Summe Tag',
  },
  {
    accessor: 'abweichung_tag',
    Header: 'Abweichung',
    Cell: data => {
      const summe_monat = Number(data?.row?.original?.summe_monat)
      const summe_tag = Number(data?.row?.original?.summe_tag)
      const diff = 100 - Math.round((summe_tag / summe_monat) * 100)

      return `${diff} %`
    },
  },
  {
    accessor: 'summe_stunden',
    Header: 'Summe Stunden',
  },
  {
    accessor: 'abweichung_stunden',
    Header: 'Abweichung',
    Cell: data => {
      const summe_monat = Number(data?.row?.original?.summe_monat)
      const summe_stunden = Number(data?.row?.original?.summe_stunden)
      const diff = 100 - Math.round((summe_stunden / summe_monat) * 100)

      return `${diff} %`
    },
  },
]

export const Calculon = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [year, setYear] = useState(new Date().getFullYear())

  const [reqMonth, setReqMonth] = useState(null)
  const [reqYear, setReqYear] = useState(null)

  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)

  const { data: calculations, isLoading } = useSensorCalculations(
    reqMonth,
    reqYear,
    limit,
    offset
  )

  const requestCalculations = () => {
    setReqMonth(month)
    setReqYear(year)
  }

  const [actionMenu, setActionMenu] = useState(null)

  const data = useMemo(
    () => (calculations?.items ? calculations?.items.data : []),
    [calculations?.items]
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns: useMemo(() => columns, []),
        data,
        initialState: { pageSize: data.length },
      },
      useFilters,
      useSortBy
    )

  return (
    <>
      <Preloader isLoading={isLoading} />

      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={'Sensoren'}
        onClose={() => setActionMenu(null)}
        open={Boolean(actionMenu)}
        historyUrlTarget={'sensors'}
      >
        <Divider />
      </AM>
      {/* action menu end */}

      <KPage
        action={
          <IconButton onClick={event => setActionMenu(event.currentTarget)}>
            <MoreVertTwoTone />
          </IconButton>
        }
        avatar={
          <Avatar>
            <PollTwoTone />
          </Avatar>
        }
        isLoading={isLoading}
        title={'Abweichungen aggregierter Daten'}
      >
        <HStack>
          <KTextField
            defaultValue={`${year}-${month < 10 ? '0' + month : month}`}
            onChange={event => {
              const _DATE = event.target.value.split('-')

              setMonth(Number(_DATE[1]))
              setYear(Number(_DATE[0]))
            }}
            label={'Zeitraum'}
            type={'month'}
          />

          <KTextField
            label={'Limit'}
            onChange={event => setLimit(Number(event.target.value))}
            type={'number'}
            value={limit}
          />

          <KTextField
            label={'Offset'}
            onChange={event => setOffset(Number(event.target.value))}
            type={'number'}
            value={offset}
          />

          <Button disabled={isLoading} onClick={requestCalculations}>
            Laden
          </Button>
        </HStack>

        {calculations?.items && (
          <TableContainer>
            <Table {...getTableProps()} sx={{ width: 1 }}>
              <TableHead>
                {headerGroups.map(headerGroup => (
                  <TableRow
                    key={headerGroup.id}
                    {...headerGroup.getHeaderGroupProps()}
                  >
                    {headerGroup.headers.map(column => (
                      <TableCell
                        key={column.id}
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                      >
                        {column.render('Header')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>

              <TableBody {...getTableBodyProps()}>
                {rows.map(row => {
                  prepareRow(row)
                  return (
                    <TableRow key={row.id} {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <TableCell
                          key={`${row.id}-${cell.column.id}`}
                          {...cell.getCellProps()}
                        >
                          {cell.render('Cell')}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </KPage>
    </>
  )
}
